import { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import {
	SHIPPING_CARRIERS,
	SHIPPING_PIPELINE,
	TERMINAL_STATUSES,
	getStatusMeta,
	getTransitionLabel,
	updateOrderStatus,
	formatCurrency,
	formatDateTime,
} from "../../api/orders";

const StatusBadge = ({ status }) => {
	const meta = getStatusMeta(status);
	return <span className={`order-status ${meta.variant}`}>{meta.label}</span>;
};

// Kargolu siparişin hangi adımda olduğunu gösteren ilerleme çubuğu
const ShippingStepper = ({ status }) => {
	const isTerminal = TERMINAL_STATUSES.includes(status);
	const currentIndex = SHIPPING_PIPELINE.indexOf(status);
	return (
		<ol className={`order-stepper ${isTerminal ? "terminal" : ""}`}>
			{SHIPPING_PIPELINE.map((key, index) => {
				let state = "upcoming";
				if (!isTerminal) {
					if (index < currentIndex) state = "done";
					else if (index === currentIndex) state = "current";
				}
				return (
					<li key={key} className={state}>
						<span className="order-stepper-dot">{state === "done" ? "✓" : index + 1}</span>
						<span className="order-stepper-label">{getStatusMeta(key).label}</span>
					</li>
				);
			})}
		</ol>
	);
};

const OrderDetailModal = ({ order, onClose, onStatusChanged }) => {
	const [pending, setPending] = useState(null); // onay bekleyen hedef durum
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [carrier, setCarrier] = useState(SHIPPING_CARRIERS[0]);
	const [trackingNumber, setTrackingNumber] = useState("");
	const [trackingError, setTrackingError] = useState("");

	useEffect(() => {
		setPending(null);
		setError("");
		setTrackingNumber("");
		setTrackingError("");
		setCarrier(SHIPPING_CARRIERS[0]);
	}, [order]);

	if (!order) return null;

	const currency = order.currency || "TRY";
	const buyer = order.buyer || {};
	const buyerName = [buyer.name, buyer.surname].filter(Boolean).join(" ") || "-";
	const nextStatuses = Array.isArray(order.nextStatuses) ? order.nextStatuses : [];
	const isTerminal = TERMINAL_STATUSES.includes(order.status);
	const progressNext = nextStatuses.filter((s) => !TERMINAL_STATUSES.includes(s));
	const dangerNext = nextStatuses.filter((s) => TERMINAL_STATUSES.includes(s));
	const history = Array.isArray(order.statusHistory) ? order.statusHistory : [];

	const needsTracking = pending === "shipped";

	const handleConfirm = async () => {
		if (!pending) return;
		let extra = {};
		if (needsTracking) {
			const tn = trackingNumber.trim();
			if (!tn) {
				setTrackingError("Kargo takip numarası zorunludur.");
				return;
			}
			extra = { trackingNumber: tn, carrier };
		}
		setSaving(true);
		setError("");
		setTrackingError("");
		try {
			const result = await updateOrderStatus(order._id, pending, extra);
			setPending(null);
			onStatusChanged?.(result.order || { ...order, status: pending, nextStatuses: [] });
		} catch (err) {
			setError(err.message || "Sipariş durumu güncellenemedi");
			setPending(null);
		} finally {
			setSaving(false);
		}
	};

	return (
		<>
			<Modal show={!!order} onHide={onClose} size="lg" className="order-detail-modal">
				<Modal.Header closeButton>
					<Modal.Title>
						Sipariş {order.orderNumber || "-"} <StatusBadge status={order.status} />
						{order.requiresShipping ? (
							<span className="order-kind shipping">Kargolu</span>
						) : (
							<span className="order-kind digital">Kargosuz</span>
						)}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{order.requiresShipping && <ShippingStepper status={order.status} />}

					{/* Durum ilerletme */}
					<div className="order-actions">
						{isTerminal ? (
							<p className="order-actions-note">
								Bu sipariş <StatusBadge status={order.status} /> durumunda. Bu durumdan sonra
								değişiklik yapılamaz.
							</p>
						) : nextStatuses.length === 0 ? (
							<p className="order-actions-note">
								Bu sipariş için ilerletilecek adım kalmadı.
							</p>
						) : (
							<>
								<span className="order-actions-label">Sonraki adım:</span>
								{progressNext.map((s) => (
									<Button key={s} variant="primary" onClick={() => setPending(s)} disabled={saving}>
										{getTransitionLabel(s)} →
									</Button>
								))}
								{dangerNext.map((s) => (
									<Button
										key={s}
										variant="outline-danger"
										onClick={() => setPending(s)}
										disabled={saving}
									>
										{getTransitionLabel(s)}
									</Button>
								))}
							</>
						)}
					</div>
					{error && <p className="text-danger mt-2">{error}</p>}

					<div className="order-detail-grid mt-3">
						<section>
							<h6>Sipariş Bilgileri</h6>
							<dl>
								<dt>Sipariş No</dt>
								<dd>{order.orderNumber || "-"}</dd>
								<dt>Ödeme Kimliği</dt>
								<dd className="mono">{order.paymentId || "-"}</dd>
								<dt>Görüşme Kimliği</dt>
								<dd className="mono">{order.conversationId || "-"}</dd>
								<dt>Ödeme Tarihi</dt>
								<dd>{formatDateTime(order.paidAt)}</dd>
								<dt>Kayıt Tarihi</dt>
								<dd>{formatDateTime(order.createdAt)}</dd>
								<dt>Kullanıcı</dt>
								<dd>{order.username || "-"}</dd>
							</dl>
						</section>
						<section>
							<h6>Alıcı</h6>
							<dl>
								<dt>Ad Soyad</dt>
								<dd>{buyerName}</dd>
								<dt>E-posta</dt>
								<dd>{buyer.email || "-"}</dd>
								<dt>Telefon</dt>
								<dd>{buyer.phone || "-"}</dd>
								<dt>Adres</dt>
								<dd>
									{[buyer.address, buyer.city, buyer.zipCode].filter(Boolean).join(", ") || "-"}
								</dd>
							</dl>
							<h6 className="mt-3">Ödeme</h6>
							<dl>
								<dt>Kart</dt>
								<dd>
									{order.cardAssociation || "Kart"}{" "}
									{order.cardLastFour ? `**** ${order.cardLastFour}` : ""}
								</dd>
							</dl>
							{order.shipment?.trackingNumber && (
								<>
									<h6 className="mt-3">Kargo</h6>
									<dl>
										<dt>Firma</dt>
										<dd>{order.shipment.carrier || "-"}</dd>
										<dt>Takip No</dt>
										<dd className="mono order-tracking">{order.shipment.trackingNumber}</dd>
										<dt>Kargoya Veriliş</dt>
										<dd>{formatDateTime(order.shipment.shippedAt)}</dd>
									</dl>
								</>
							)}
						</section>
					</div>

					<h6 className="mt-3">Kalemler</h6>
					<Table bordered size="sm" responsive className="order-items-table">
						<thead>
							<tr>
								<th>Ürün</th>
								<th className="num">Birim Fiyat</th>
								<th className="num">Adet</th>
								<th className="num">Token</th>
								<th className="num">Toplam</th>
							</tr>
						</thead>
						<tbody>
							{(order.items || []).length === 0 ? (
								<tr>
									<td colSpan={5} className="text-center text-muted">
										Kalem bulunmuyor.
									</td>
								</tr>
							) : (
								order.items.map((item, index) => (
									<tr key={item.id || index}>
										<td>{item.name || "-"}</td>
										<td className="num">{formatCurrency(item.unitPrice, currency)}</td>
										<td className="num">{item.quantity ?? "-"}</td>
										<td className="num">{item.tokens ?? "-"}</td>
										<td className="num">{formatCurrency(item.total, currency)}</td>
									</tr>
								))
							)}
						</tbody>
						<tfoot>
							<tr>
								<td colSpan={4} className="num">Ara Toplam</td>
								<td className="num">{formatCurrency(order.subtotal, currency)}</td>
							</tr>
							<tr>
								<td colSpan={4} className="num">KDV</td>
								<td className="num">{formatCurrency(order.tax, currency)}</td>
							</tr>
							<tr className="order-total-row">
								<td colSpan={4} className="num">Toplam</td>
								<td className="num">{formatCurrency(order.total, currency)}</td>
							</tr>
							<tr>
								<td colSpan={4} className="num">Satın Alınan Token</td>
								<td className="num">{order.tokensPurchased ?? 0}</td>
							</tr>
						</tfoot>
					</Table>

					{history.length > 0 && (
						<>
							<h6 className="mt-3">Durum Geçmişi</h6>
							<ul className="order-history">
								{[...history].reverse().map((h, i) => (
									<li key={i}>
										<StatusBadge status={h.status} />
										<span>{formatDateTime(h.at)}</span>
										{h.by && <small>{h.by === "shop" ? "mağaza" : h.by}</small>}
									</li>
								))}
							</ul>
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={onClose}>
						Kapat
					</Button>
				</Modal.Footer>
			</Modal>

			<Modal show={!!pending} onHide={() => !saving && setPending(null)} centered>
				<Modal.Header closeButton>
					{pending && TERMINAL_STATUSES.includes(pending) ? "Emin misiniz?" : "Adımı Onayla"}
				</Modal.Header>
				<Modal.Body>
					<p>
						<strong>{order.orderNumber}</strong> numaralı sipariş{" "}
						<StatusBadge status={order.status} /> durumundan{" "}
						<StatusBadge status={pending} /> durumuna geçirilecek.
					</p>
					{needsTracking && (
						<div className="order-tracking-form">
							<Form.Group>
								<label className="mb-1">Kargo Firması</label>
								<Form.Select value={carrier} onChange={(e) => setCarrier(e.target.value)}>
									{SHIPPING_CARRIERS.map((c) => (
										<option value={c} key={c}>
											{c}
										</option>
									))}
								</Form.Select>
							</Form.Group>
							<Form.Group>
								<label className="mb-1">Kargo Takip Numarası *</label>
								<Form.Control
									type="text"
									placeholder="Örn: 1234567890123"
									value={trackingNumber}
									maxLength={64}
									autoFocus
									isInvalid={!!trackingError}
									onChange={(e) => {
										setTrackingNumber(e.target.value);
										if (trackingError) setTrackingError("");
									}}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											e.preventDefault();
											handleConfirm();
										}
									}}
								/>
								{trackingError && (
									<Form.Control.Feedback type="invalid">{trackingError}</Form.Control.Feedback>
								)}
							</Form.Group>
						</div>
					)}
					<p className="mb-0 text-muted">
						{pending && TERMINAL_STATUSES.includes(pending)
							? "Bu işlem kalıcıdır; sipariş bu durumdan sonra bir daha değiştirilemez."
							: "Bu işlem geri alınamaz; sipariş önceki adıma döndürülemez."}
					</p>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => setPending(null)} disabled={saving}>
						Vazgeç
					</Button>
					<Button
						variant={pending && TERMINAL_STATUSES.includes(pending) ? "danger" : "primary"}
						onClick={handleConfirm}
						disabled={saving || (needsTracking && !trackingNumber.trim())}
					>
						{saving ? (
							<>
								<Spinner animation="border" size="sm" style={{ marginRight: "8px" }} />
								Kaydediliyor...
							</>
						) : (
							"Onayla"
						)}
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export { StatusBadge };
export default OrderDetailModal;
